import type { Language } from "@/data/translations";

/** Public contact for BRG claims (same as site footer). */
export const BRG_CLAIM_EMAIL = "info@beatenberg-resortspa.ch";

export interface BrgFAQ {
  question: string;
  answer: string[];
}

export interface BrgSection {
  id: string;
  title: string;
  faqs: BrgFAQ[];
}

export interface BestRateGuaranteeCopy {
  heroTitle: string;
  heroLead: string;
  intro: string[];
  sections: BrgSection[];
  contactPrompt: string;
  /** mailto: subject= */
  emailSubject: string;
  bookOfficialCta: string;
  bookOfficialNote: string;
}

const en: BestRateGuaranteeCopy = {
  heroTitle: "Best Rate Guarantee",
  heroLead:
    "If a lower published rate for Beatenberg Resort & Spa is found on an online travel agency (OTA) or other third-party booking website or application (a “Competing Rate”) that meets the criteria below, and a valid claim is submitted in accordance with these terms, the hotel will satisfy an approved claim by issuing a credit note for the difference between the prepaid room rate on the hotel’s official website and the Competing Rate, for the same room type, rate plan, occupancy, dates, and length of stay.",
  intro: [
    "The programme applies to Beatenberg Resort & Spa only. Affiliated properties, partner hotels, and other brands are excluded.",
    "Eligibility is limited to prepaid reservations completed on the hotel’s official website. Reservations by telephone, e-mail, or any offline channel are excluded. Reservations made on third-party websites or applications are excluded.",
  ],
  sections: [
    {
      id: "definition",
      title: "Definition",
      faqs: [
        {
          question: "Does the Best Rate Guarantee only apply to certain rates?",
          answer: [
            "Yes. It applies to published rates available to the general public on online channels. It does not apply to group or contracted rates, packaged or opaque rates, or qualified membership rates such as TCS member rates, Hotelcard rates, or official government rates.",
          ],
        },
        {
          question: "Must the Competing Rate match the reservation on the official website exactly?",
          answer: [
            "Yes. The Best Rate Guarantee applies only where the Competing Rate is offered for the same hotel, room type, rate plan, number of guests, stay dates, and length of stay, and on the same cancellation and flexibility terms, with the same inclusions (including meals or bundled services), as the guest’s reservation on the official website.",
          ],
        },
        {
          question: "Are taxes, service charges, and other fees included in the Best Rate Guarantee?",
          answer: [
            "No. The Best Rate Guarantee does not include taxes, gratuities, service charges, resort or facility fees, or other additional charges or fees, unless such amounts are identical and itemised on both the official website booking and the third-party final booking page.",
          ],
        },
        {
          question: "Does the Best Rate Guarantee apply where the difference is due to currency exchange only?",
          answer: [
            "No. If the rate discrepancy is solely or principally attributable to currency conversion or rounding, the hotel reserves the right to deny the claim.",
          ],
        },
      ],
    },
    {
      id: "process",
      title: "Process",
      faqs: [
        {
          question: "How do I submit a Best Rate Guarantee claim?",
          answer: [
            `A claim must be submitted by e-mail to ${BRG_CLAIM_EMAIL} within twenty-four (24) hours of completing the reservation on the hotel’s official website. The e-mail must include the guest’s reservation confirmation details, a direct URL to the third-party final booking page displaying the Competing Rate, and a PDF export of that final booking page.`,
          ],
        },
        {
          question: "What is acceptable evidence of a lower Competing Rate?",
          answer: [
            "Acceptable evidence consists of the URL and PDF described above. Beatenberg Resort & Spa has sole discretion to determine the validity of any claim and reserves the right to deny a claim if the Competing Rate cannot be independently verified at the time of processing.",
          ],
        },
      ],
    },
    {
      id: "validity",
      title: "Validity",
      faqs: [
        {
          question: "Do I need to make a reservation on the official website to submit a Best Rate Guarantee claim?",
          answer: [
            "Yes. A valid prepaid reservation booked through the hotel’s official website is required.",
          ],
        },
        {
          question: "If my reservation is prepaid and I make a valid Best Rate Guarantee claim, how is the benefit provided?",
          answer: [
            "Rates on the official website are prepaid. Where a claim is approved, the difference between the room rate paid on the official website and the qualifying Competing Rate will be satisfied by issuance of a credit note, determined in accordance with these terms.",
          ],
        },
        {
          question: "I made a booking on an OTA. Am I eligible for the Best Rate Guarantee?",
          answer: [
            "No. Only reservations made on the hotel’s official website are eligible.",
          ],
        },
        {
          question: "What if I find a lower rate after the claim deadline?",
          answer: [
            "No. Claims must be submitted within twenty-four (24) hours of making the reservation on the official website.",
          ],
        },
        {
          question: "What happens to the Best Rate Guarantee in the event of a technical issue?",
          answer: [
            "The Best Rate Guarantee is suspended during outages, technical failures, or circumstances beyond the hotel’s reasonable control.",
          ],
        },
        {
          question: "Where should enquiries relating to a claim be directed?",
          answer: [
            `Enquiries must be directed to ${BRG_CLAIM_EMAIL} and must include the reservation reference.`,
          ],
        },
      ],
    },
    {
      id: "claim",
      title: "Claim",
      faqs: [
        {
          question: "Is there a minimum difference between rates for a claim to qualify?",
          answer: [
            "Yes. The hotel may deny a claim where the Competing Rate is less than two and one-half percent (2.5%) below the corresponding room rate on the official website on a like-for-like basis before taxes and separate mandatory fees, or where any other eligibility requirement is not met.",
          ],
        },
        {
          question: "Is there a chance my claim might be denied?",
          answer: [
            "Yes. The hotel may deny any claim that does not meet these criteria, including where independent verification is not possible or the offer is not equivalent.",
          ],
        },
        {
          question: "Who reserves the right to determine the validity of a claim?",
          answer: [
            "Beatenberg Resort & Spa, in its sole discretion.",
          ],
        },
      ],
    },
    {
      id: "legal",
      title: "Dispute",
      faqs: [
        {
          question: "What is the procedure if a dispute arises?",
          answer: [
            "These terms are governed by the laws of Switzerland. The exclusive place of jurisdiction for disputes arising out of or in connection with the Best Rate Guarantee shall be the courts of the Canton of Bern, Switzerland.",
          ],
        },
        {
          question: "Who holds the right to revise, amend, or suspend the Best Rate Guarantee?",
          answer: [
            "The hotel reserves the right to revise, amend, supplement, suspend, or discontinue the Best Rate Guarantee at any time, at its sole discretion and without prior notice.",
          ],
        },
      ],
    },
  ],
  contactPrompt: "Claim submission (e-mail)",
  emailSubject: "Best Rate Guarantee claim",
  bookOfficialCta: "Official website booking",
  bookOfficialNote:
    " — eligibility is limited to prepaid reservations completed on the official website.",
};

const de: BestRateGuaranteeCopy = {
  heroTitle: "Bestpreisgarantie",
  heroLead:
    "Wird für das Beatenberg Resort & Spa auf einer Online-Reiseagentur (OTA) oder einer sonstigen Buchungswebsite oder -anwendung eines Dritten ein niedrigerer veröffentlichter Tarif gefunden, der die nachstehenden Kriterien erfüllt (Konkurrenztarif), und wird ein gültiger Anspruch gemäss diesen Bedingungen eingereicht, erfüllt das Hotel einen bewilligten Anspruch durch Ausstellung einer Gutschrift über die Differenz zwischen dem im Voraus bezahlten Zimmerpreis auf der offiziellen Website des Hotels und dem Konkurrenztarif, und zwar für denselben Zimmertyp, Tarifplan, Belegung, Daten und Aufenthaltsdauer.",
  intro: [
    "Das Programm gilt ausschließlich für das Beatenberg Resort & Spa. Beteiligte Unterkünfte, Partnerhotels und sonstige Marken sind ausgeschlossen.",
    "Die Berechtigung beschränkt sich auf im Voraus bezahlte Reservierungen, die über die offizielle Website des Hotels abgeschlossen werden. Reservierungen per Telefon, E-Mail oder sonstigem Offline-Kanal sind ausgeschlossen. Reservierungen über Websites oder Anwendungen Dritter sind ausgeschlossen.",
  ],
  sections: [
    {
      id: "definition",
      title: "Definition",
      faqs: [
        {
          question: "Gilt die Bestpreisgarantie nur für bestimmte Tarife?",
          answer: [
            "Ja. Sie gilt für veröffentlichte Tarife, die der Allgemeinheit auf Online-Kanälen zur Verfügung stehen. Sie gilt nicht für Gruppen- oder Vertragstarife, Paket- oder Opak-Tarife oder qualifizierte Mitgliedstarife wie TCS-Mitgliedertarife, Hotelcard-Tarife oder offizielle Behördenraten.",
          ],
        },
        {
          question: "Muss der Konkurrenztarif der Reservierung auf der offiziellen Website exakt entsprechen?",
          answer: [
            "Ja. Die Bestpreisgarantie gilt nur, wenn der Konkurrenztarif für dasselbe Hotel, denselben Zimmertyp, denselben Tarifplan, dieselbe Gästezahl, dieselben Aufenthaltsdaten und dieselbe Aufenthaltsdauer sowie zu denselben Stornierungs- und Flexibilitätsbedingungen und mit denselben Leistungen (einschliesslich Mahlzeiten oder gebündelter Leistungen) angeboten wird wie die Reservierung des Gastes auf der offiziellen Website.",
          ],
        },
        {
          question: "Sind Steuern, Servicegebühren und sonstige Entgelte in der Bestpreisgarantie enthalten?",
          answer: [
            "Nein. Die Bestpreisgarantie umfasst keine Steuern, Trinkgelder, Servicegebühren, Resort- oder Anlagegebühren oder sonstige Zuschläge, sofern diese Beträge nicht auf der Buchung über die offizielle Website und auf der Schlussbuchungsseite des Dritten identisch und ausgewiesen sind.",
          ],
        },
        {
          question: "Gilt die Bestpreisgarantie, wenn die Differenz allein auf dem Währungsumtausch beruht?",
          answer: [
            "Nein. Liegt die Tarifdifferenz ausschliesslich oder im Wesentlichen an Währungsumrechnung oder Rundung, behält sich das Hotel das Recht vor, den Anspruch abzulehnen.",
          ],
        },
      ],
    },
    {
      id: "process",
      title: "Verfahren",
      faqs: [
        {
          question: "Wie reiche ich einen Anspruch auf Bestpreisgarantie ein?",
          answer: [
            `Der Anspruch ist per E-Mail an ${BRG_CLAIM_EMAIL} innerhalb von vierundzwanzig (24) Stunden nach Abschluss der Reservierung auf der offiziellen Website des Hotels einzureichen. Die E-Mail muss die Bestätigungsangaben zur Reservierung des Gastes, eine direkte URL zur Schlussbuchungsseite des Dritten mit Anzeige des Konkurrenztarifs sowie ein PDF-Export dieser Schlussbuchungsseite enthalten.`,
          ],
        },
        {
          question: "Was gilt als Nachweis für einen niedrigeren Konkurrenztarif?",
          answer: [
            "Als Nachweis gelten die vorgenannte URL und das PDF. Das Beatenberg Resort & Spa entscheidet in eigenem Ermessen über die Gültigkeit jedes Anspruchs und behält sich vor, einen Anspruch abzulehnen, wenn der Konkurrenztarif zum Zeitpunkt der Bearbeitung nicht unabhängig verifiziert werden kann.",
          ],
        },
      ],
    },
    {
      id: "validity",
      title: "Gültigkeit",
      faqs: [
        {
          question: "Muss ich auf der offiziellen Website reservieren, um einen Anspruch auf Bestpreisgarantie geltend zu machen?",
          answer: [
            "Ja. Eine gültige, im Voraus bezahlte Reservierung über die offizielle Website des Hotels ist erforderlich.",
          ],
        },
        {
          question: "Wie wird der Vorteil gewährt, wenn meine Reservierung im Voraus bezahlt ist und mein Anspruch auf Bestpreisgarantie gültig ist?",
          answer: [
            "Die Tarife auf der offiziellen Website sind im Voraus zu bezahlen. Bei Bewilligung eines Anspruchs wird die Differenz zwischen dem auf der offiziellen Website bezahlten Zimmerpreis und dem qualifizierten Konkurrenztarif durch Ausstellung einer Gutschrift abgegolten, gemäss diesen Bedingungen.",
          ],
        },
        {
          question: "Ich habe über eine OTA gebucht. Besteht Anspruch auf die Bestpreisgarantie?",
          answer: [
            "Nein. Nur Reservierungen über die offizielle Website des Hotels sind berechtigt.",
          ],
        },
        {
          question: "Was gilt, wenn ich nach Ablauf der Frist einen niedrigeren Tarif finde?",
          answer: [
            "Nein. Ansprüche müssen innerhalb von vierundzwanzig (24) Stunden nach der Reservierung auf der offiziellen Website eingereicht werden.",
          ],
        },
        {
          question: "Was geschieht bei technischen Störungen?",
          answer: [
            "Die Bestpreisgarantie ist während Ausfällen, technischen Störungen oder Umständen ausserhalb des zumutbaren Einflussbereichs des Hotels ausgesetzt.",
          ],
        },
        {
          question: "Wohin sind Anfragen zu einem Anspruch zu richten?",
          answer: [
            `Anfragen sind an ${BRG_CLAIM_EMAIL} zu richten und müssen die Reservierungsreferenz enthalten.`,
          ],
        },
      ],
    },
    {
      id: "claim",
      title: "Anspruch",
      faqs: [
        {
          question: "Gibt es eine Mindestdifferenz zwischen den Tarifen, damit ein Anspruch qualifiziert?",
          answer: [
            "Ja. Das Hotel kann einen Anspruch ablehnen, wenn der Konkurrenztarif bei vergleichbarer Leistung nicht mindestens zwei Komma fünf Prozent (2,5 %) unter dem entsprechenden Zimmerpreis auf der offiziellen Website liegt, bezogen vor Steuern und gesonderten obligatorischen Gebühren, oder wenn eine sonstige Voraussetzung nicht erfüllt ist.",
          ],
        },
        {
          question: "Kann mein Anspruch abgelehnt werden?",
          answer: [
            "Ja. Das Hotel kann jeden Anspruch ablehnen, der diese Kriterien nicht erfüllt, einschliesslich wenn eine unabhängige Verifizierung nicht möglich ist oder das Angebot nicht gleichwertig ist.",
          ],
        },
        {
          question: "Wer behält sich das Recht vor, die Gültigkeit eines Anspruchs festzustellen?",
          answer: [
            "Das Beatenberg Resort & Spa, in eigenem Ermessen.",
          ],
        },
      ],
    },
    {
      id: "legal",
      title: "Streitigkeiten",
      faqs: [
        {
          question: "Welches Verfahren gilt bei Streitigkeiten?",
          answer: [
            "Diese Bedingungen unterstehen schweizerischem Recht. Ausschliesslicher Gerichtsstand für Streitigkeiten aus oder im Zusammenhang mit der Bestpreisgarantie sind die Gerichte des Kantons Bern, Schweiz.",
          ],
        },
        {
          question: "Wer kann die Bestpreisgarantie ändern, ergänzen oder aussetzen?",
          answer: [
            "Das Hotel behält sich das Recht vor, die Bestpreisgarantie jederzeit nach eigenem Ermessen zu ändern, zu ergänzen, auszusetzen oder einzustellen, ohne vorherige Ankündigung.",
          ],
        },
      ],
    },
  ],
  contactPrompt: "Einreichung des Anspruchs (E-Mail)",
  emailSubject: "Bestpreisgarantie – Anspruch",
  bookOfficialCta: "Buchung über die offizielle Website",
  bookOfficialNote:
    " — die Berechtigung beschränkt sich auf im Voraus bezahlte Reservierungen über die offizielle Website.",
};

const fr: BestRateGuaranteeCopy = {
  heroTitle: "Garantie du meilleur tarif",
  heroLead:
    "Si un tarif publié inférieur pour le Beatenberg Resort & Spa est trouvé sur une agence de voyages en ligne (OTA) ou sur un autre site ou application de réservation tiers (un « tarif concurrent ») qui satisfait aux critères ci-dessous, et si une réclamation valide est soumise conformément aux présentes conditions, l’hôtel satisfera une réclamation approuvée par l’émission d’un avoir correspondant à la différence entre le tarif chambre prépayé sur le site officiel de l’hôtel et le tarif concurrent, pour le même type de chambre, plan tarifaire, occupation, dates et durée de séjour.",
  intro: [
    "Le programme s’applique uniquement au Beatenberg Resort & Spa. Les établissements affiliés, hôtels partenaires et autres enseignes sont exclus.",
    "L’admissibilité est limitée aux réservations prépayées finalisées sur le site officiel de l’hôtel. Les réservations par téléphone, courriel ou tout canal hors ligne sont exclues. Les réservations effectuées sur des sites ou applications tiers sont exclues.",
  ],
  sections: [
    {
      id: "definition",
      title: "Définition",
      faqs: [
        {
          question: "La garantie du meilleur tarif ne s’applique-t-elle qu’à certains tarifs ?",
          answer: [
            "Oui. Elle s’applique aux tarifs publiés accessibles au grand public sur les canaux en ligne. Elle ne s’applique pas aux tarifs de groupe ou contractuels, aux forfaits ou tarifs opaques, ni aux tarifs réservés aux membres qualifiés tels que les tarifs membres TCS, les tarifs Hotelcard ou les tarifs officiels pour le secteur public.",
          ],
        },
        {
          question: "Le tarif concurrent doit-il correspondre exactement à la réservation sur le site officiel ?",
          answer: [
            "Oui. La garantie du meilleur tarif ne s’applique que si le tarif concurrent porte sur le même hôtel, le même type de chambre, le même plan tarifaire, le même nombre de clients, les mêmes dates et la même durée de séjour, aux mêmes conditions d’annulation et de flexibilité et avec les mêmes prestations incluses (notamment repas ou services groupés) que la réservation du client sur le site officiel.",
          ],
        },
        {
          question: "Les taxes, frais de service et autres montants sont-ils inclus dans la garantie du meilleur tarif ?",
          answer: [
            "Non. La garantie du meilleur tarif n’inclut pas les taxes, pourboires, frais de service, frais d’établissement ou autres suppléments, sauf si ces montants sont identiques et ventilés sur la réservation via le site officiel et sur la page finale de réservation du tiers.",
          ],
        },
        {
          question: "La garantie du meilleur tarif s’applique-t-elle lorsque l’écart résulte uniquement du change ?",
          answer: [
            "Non. Si l’écart de tarif résulte uniquement ou principalement du calcul ou de l’arrondi des changes, l’hôtel se réserve le droit de refuser la réclamation.",
          ],
        },
      ],
    },
    {
      id: "process",
      title: "Procédure",
      faqs: [
        {
          question: "Comment soumettre une réclamation au titre de la garantie du meilleur tarif ?",
          answer: [
            `La réclamation doit être soumise par courriel à l’adresse ${BRG_CLAIM_EMAIL} dans les vingt-quatre (24) heures suivant la finalisation de la réservation sur le site officiel de l’hôtel. Le courriel doit inclure les détails de confirmation de réservation du client, une URL directe vers la page finale de réservation du tiers affichant le tarif concurrent, et une exportation PDF de cette page finale.`,
          ],
        },
        {
          question: "Qu’est-ce qui constitue une preuve acceptable d’un tarif concurrent inférieur ?",
          answer: [
            "La preuve acceptable consiste en l’URL et le PDF visés ci-dessus. Le Beatenberg Resort & Spa a seul pouvoir d’apprécier la validité de toute réclamation et se réserve le droit de la rejeter si le tarif concurrent ne peut être vérifié de façon indépendante au moment du traitement.",
          ],
        },
      ],
    },
    {
      id: "validity",
      title: "Validité",
      faqs: [
        {
          question: "Dois-je avoir effectué une réservation sur le site officiel pour soumettre une réclamation ?",
          answer: [
            "Oui. Une réservation prépayée valide passée par le site officiel de l’hôtel est requise.",
          ],
        },
        {
          question: "Si ma réservation est prépayée et que je soumets une réclamation valide au titre de la garantie du meilleur tarif, comment le bénéfice est-il accordé ?",
          answer: [
            "Les tarifs sur le site officiel sont prépayés. Lorsqu’une réclamation est approuvée, la différence entre le tarif chambre payé sur le site officiel et le tarif concurrent admissible est réglée par émission d’un avoir, déterminé conformément aux présentes conditions.",
          ],
        },
        {
          question: "J’ai effectué une réservation sur une OTA. Suis-je admissible à la garantie du meilleur tarif ?",
          answer: [
            "Non. Seules les réservations passées sur le site officiel de l’hôtel sont admissibles.",
          ],
        },
        {
          question: "Que se passe-t-il si je trouve un tarif inférieur après l’expiration du délai de réclamation ?",
          answer: [
            "Non. Les réclamations doivent être soumises dans les vingt-quatre (24) heures suivant la réservation sur le site officiel.",
          ],
        },
        {
          question: "Que devient la garantie du meilleur tarif en cas de problème technique ?",
          answer: [
            "La garantie du meilleur tarif est suspendue pendant les pannes, dysfonctionnements techniques ou circonstances indépendantes de la volonté raisonnable de l’hôtel.",
          ],
        },
        {
          question: "À quelle adresse les demandes relatives à une réclamation doivent-elles être adressées ?",
          answer: [
            `Les demandes doivent être adressées à ${BRG_CLAIM_EMAIL} et comporter la référence de réservation.`,
          ],
        },
      ],
    },
    {
      id: "claim",
      title: "Réclamation",
      faqs: [
        {
          question: "Existe-t-il un écart minimum entre les tarifs pour qu’une réclamation soit recevable ?",
          answer: [
            "Oui. L’hôtel peut rejeter une réclamation lorsque le tarif concurrent n’est pas inférieur d’au moins deux virgule cinq pour cent (2,5 %) au tarif chambre correspondant sur le site officiel pour une offre strictement comparable, avant taxes et frais obligatoires distincts, ou lorsqu’une autre condition d’admissibilité n’est pas remplie.",
          ],
        },
        {
          question: "Une réclamation peut-elle être refusée ?",
          answer: [
            "Oui. L’hôtel peut refuser toute réclamation qui ne satisfait pas à ces critères, y compris lorsque la vérification indépendante est impossible ou que l’offre n’est pas équivalente.",
          ],
        },
        {
          question: "Qui détient le droit de déterminer la validité d’une réclamation ?",
          answer: [
            "Le Beatenberg Resort & Spa, à sa seule discrétion.",
          ],
        },
      ],
    },
    {
      id: "legal",
      title: "Litiges",
      faqs: [
        {
          question: "Quelle est la procédure en cas de litige ?",
          answer: [
            "Les présentes conditions sont régies par le droit suisse. Le for exclusif pour tout litige découlant de ou lié à la garantie du meilleur tarif est celui des tribunaux du canton de Berne, Suisse.",
          ],
        },
        {
          question: "Qui détient le droit de réviser, modifier ou suspendre la garantie du meilleur tarif ?",
          answer: [
            "L’hôtel se réserve le droit de réviser, modifier, compléter, suspendre ou supprimer la garantie du meilleur tarif à tout moment, à sa seule discrétion et sans préavis.",
          ],
        },
      ],
    },
  ],
  contactPrompt: "Soumission de la réclamation (courriel)",
  emailSubject: "Réclamation — garantie du meilleur tarif",
  bookOfficialCta: "Réservation sur le site officiel",
  bookOfficialNote:
    " — l’admissibilité est limitée aux réservations prépayées finalisées sur le site officiel.",
};

export function getBestRateGuaranteeCopy(language: Language): BestRateGuaranteeCopy {
  if (language === "de") return de;
  if (language === "fr") return fr;
  return en;
}

